//libs
import get from 'lodash/get';
import { Location } from 'history';
import isEmpty from 'lodash/isEmpty';

//actions
import { ActionTypes } from './actions';

//helpers
import { fetchListingData } from 'shared/api/listingV2';
import { getStoreRedirectCount } from 'shared/api/listing';
import { validateCrossRedirection } from 'shared/components/AppRedirectionPopup/components/AppRedirectionPopup/helpers';
import {
  getIsFilteredURL,
  isSearchPage,
  transformPayload,
  appendURLParameters,
  isOfferPage,
} from './helpers';
import { getDeviceType } from 'shared/components/ResponsiveLoader/helpers';
import {
  onPaginationLoad,
  calculateCumulativeOOSCount,
} from 'shared/layouts/Listing/components/Analytics/helpers';
import logger from 'shared/logger';
import { getDomain } from 'shared/helpers/app';

//types
import { getLandingPageData, LandingPageProps } from 'shared/api/landingpage';
import { getFilterNames, transformApiResult } from './transformer';
import { transformTopBestsellers } from 'shared/components/Footer/components/BestsellersPriceList/helpers';

//selectors
import {
  getPayload,
  getLocation,
  getIsInitial,
  getProducts,
  getIsFetchingWithFilters,
  getCumulativeOOSCount,
  getCategoryId,
} from 'shared/store/listingV2/selectors';
import { getSSRHeaders, getStoreId } from 'shared/store/app/selectors';
import { buildStoreRedirectId, DEFAULT_STORE } from 'shared/helpers/storeUrl';
import { getDomainConfig } from 'shared/domainConfig/helpers';
import { getRemoteConfig } from 'shared/store/remoteConfigs/selectors';
import { getABExperimentVariant } from '@nykaa/experiment-sdk/store/selectors';

//constants
import { API_KEYS, USER_RANKING_ALGO, VECTOR_SEARCH_ALGO } from './constants';
import { RENDER_MODE } from 'shared/constants';
import {
  MWEB_NEW_RANKING,
  MWEB_VECTOR_SEARCH
} from 'shared/constants/listing';

//actions
import { redirect, setAppSearchTerm, updateStatusCode } from 'shared/store/app/actions';

export const getPLPData = (isMobile: boolean) => {
  return async (dispatch: any, getState: any) => {
    try {
      const state = getState();

      const { isEligible: vecEligible, variant: vecVariant = '' } = getABExperimentVariant(state, MWEB_VECTOR_SEARCH.EXPERIMENT_ID);
      const { isEligible: rankEligible, variant: rankVariant = '' } = getABExperimentVariant(state, MWEB_NEW_RANKING.EXPERIMENT_ID);

      const ssrHeaders = { ...getSSRHeaders(state), domain: getDomain() };
      const platformKey = getDeviceType() === RENDER_MODE.DESKTOP ? 'dweb.enabled' : 'mweb.enabled';
      const isAdPlatformEnabled = get(getRemoteConfig(state, 'plp_adplatform'), platformKey, false);

      const isInitial = getIsInitial(state);
      const previousProducts = getProducts(state);
      const isFetchingWithFilters = getIsFetchingWithFilters(state);
      const isPriceRevealEnabled = get(getRemoteConfig(state, 'priceReveal'), 'desktop', false);
      const shouldFetchAdPlatformV2 = getRemoteConfig(state, 'enableAdPlatformStoreParam');
      const storeId = getStoreId(state);
      const vectorSearchAlgo = getRemoteConfig(state, VECTOR_SEARCH_ALGO);
      const userRankingAlgo = getRemoteConfig(state, USER_RANKING_ALGO);

      const payload: Record<string, string> = getPayload(state);
      if (isPriceRevealEnabled) payload[API_KEYS.PRICE_REVEAL] = '1';

      const location = getLocation(state) as Location;

      const isSearch = isSearchPage(location.pathname);
      const params = transformPayload(payload, location, isMobile);

      if (!params) {
        return;
      }

      if (params.defaultParams[API_KEYS.SEARCH_TERM])
        dispatch(setAppSearchTerm(params.defaultParams[API_KEYS.SEARCH_TERM]));

      if (isInitial) params.defaultParams['meta'] = 1;
      if (storeId && getDomainConfig().CATALOG_TAG_PREFIX) {
        params.defaultParams[API_KEYS.CATALOG_TAG_FILTER] = buildStoreRedirectId(storeId) as string;
      }

      const experimentParams: Record<string, string> = {};
      if (vecEligible && vecVariant === MWEB_VECTOR_SEARCH.VARIANT_A) {
        const entries = Object.entries(vectorSearchAlgo || {});
        if (entries?.length > 0) {
          entries.forEach(([key, val]) => {
            if (val != null) {
              experimentParams[key] = String(val);
            }
          });
        } else {
          experimentParams.vector_search_algo = vectorSearchAlgo?.vector_search_algo;
          experimentParams.classifier_algo = vectorSearchAlgo?.classifier_algo;
        }
      }

      if (rankEligible && rankVariant === MWEB_NEW_RANKING.VARIANT_A) {
        const entries = Object.entries(userRankingAlgo || {});
        if (entries?.length > 0) {
          entries.forEach(([key, val]) => {
            if (val != null) {
              experimentParams[key] = String(val);
            }
          });
        } else {
          experimentParams.sort_algo = userRankingAlgo?.sort_algo;
        }
      }

      const listingData = await fetchListingData(
        {
          ...params.defaultParams,
          currentPage: params.currentPage,
          ...params.filters,
          ...experimentParams,
        },
        ssrHeaders
      );
      let adPlatform;

      const baseCategoryUrl = get(listingData, 'response.categoryURL');
      const baseCategoryId = get(listingData, 'response.categoryId');

      const storePathPrefix = storeId && storeId !== DEFAULT_STORE ? `/${storeId}` : '';
      const normalizedCategoryUrl =
        storePathPrefix && baseCategoryUrl?.startsWith(storePathPrefix)
          ? baseCategoryUrl.slice(storePathPrefix.length)
          : baseCategoryUrl;
      const redirectPageParams = get(listingData, 'response.redirect_page', {});
      const redirectURL = get(listingData, 'response.redirect_url.value');

      if (isEmpty(redirectPageParams) === false) {
        const isValidCrossRedirect = validateCrossRedirection(redirectPageParams);

        if (isValidCrossRedirect) {
          const redirectConfig = {
            enabled: true,
            redirectURL: redirectPageParams.id,
          };

          dispatch({
            type: ActionTypes.UPDATE_CROSS_PLATFORM_REDIRECT_CONFIG,
            payload: redirectConfig,
          });
        }
      }

      // redirect to respective URL provided by PLP API
      if (isEmpty(redirectURL) === false) {
        const redirectWithTracking = appendURLParameters(redirectURL, `&searchRedirection=1`);

        dispatch(redirect(redirectWithTracking));
        return;
      }

      if (baseCategoryUrl && normalizedCategoryUrl !== location.pathname) {
        dispatch(redirect(`${baseCategoryUrl}${location.search}`, 301));
        logger.info({
          message: `Redirection: ${location.pathname} : ${baseCategoryUrl}`,
        });

        return;
      }
      const previousCategoryId = getCategoryId(getState());
      const rawFaq = get(listingData, 'response.faq');

      const isCategory = Boolean(params.defaultParams.categoryId) && !isSearch && !isOfferPage(location.pathname);
      let faq: Array<{ que: string; ans: string }> | null = null;
      if (!isCategory) {
        faq = [];
      } else if (Array.isArray(rawFaq)) {
        faq = rawFaq;
      } else if (String(params.defaultParams.categoryId || '') !== String(previousCategoryId || '')) {
        faq = [];
      }

      // Only set on initial meta=1 load; pagination must not overwrite with empty/missing block_data
      let footerHTML: string | null = null;
      let topBestsellers = null;

      if (isInitial) {
        const blockData = get(listingData, 'response.block_data');
        if (blockData != null && blockData !== '') {
          footerHTML = String(blockData);
        } else if (typeof blockData === 'string') {
          footerHTML = blockData;
        }

        const pageName = get(listingData, 'response.ad_platform_data.page-type', '');
        const pageData = get(listingData, 'response.ad_platform_data.page-data', '');
        const count = get(listingData, 'response.count', 0);
        topBestsellers = transformTopBestsellers(
          get(listingData, 'response.top_bestsellers', [])
        );

        const adPlatformRequestParams: LandingPageProps = {
          headers: ssrHeaders,
          pageName,
          storeId,
        };

        if (!(count === 0 && !getIsFilteredURL(location.pathname))) {
          adPlatformRequestParams.pageData = pageData;
        }

        if (shouldFetchAdPlatformV2) {
          adPlatformRequestParams.shouldFetchAdPlatformV2 = true;
          adPlatformRequestParams.pageData = pageData || baseCategoryId;
        }

        if (isAdPlatformEnabled && pageName) {
          adPlatform = await getLandingPageData(adPlatformRequestParams);
        }
      }

      const filters = get(listingData, 'response.filters', []);
      let filterWithNames = [];
      filterWithNames = getFilterNames(filters, params.filters);

      if (params.currentPage !== '1' && listingData.response.products.length === 0) {
        const url = baseCategoryUrl
          ? baseCategoryUrl
          : location.pathname + location.search.split('&p=')[0];

        dispatch(redirect(url, 307));
        return;
      }

      // Calculate cumulative OOS count
      const cumulativeOOSCount = calculateCumulativeOOSCount({
        products: listingData.response.products,
        currentPage: params.currentPage,
        previousCumulativeCount: getCumulativeOOSCount(getState()),
        isFetchingWithFilters
      });

      dispatch({
        type: ActionTypes.SET_LISTING_DATA,
        payload: transformApiResult(
          {
            listingResponse: listingData.response,
            adPlatformResponse: adPlatform,
            currentPage: params.currentPage,
            appliedFilters: params.filters,
            extras: {
              isSearch,
              isAdPlatformEnabled,
              ...params.defaultParams,
              filterWithNames,
              footerHTML,
              faq,
              topBestsellers,
              isOfferPage: isOfferPage(location.pathname),
              cumulativeOOSCount
            },
          },
          isFetchingWithFilters,
          { products: previousProducts }
        ),
      });

      // Fire pagination analytics for page > 1
      if (params.currentPage && Number(params.currentPage) > 1) {
        const totalCount = listingData.response.count || 0;
        onPaginationLoad(Number(params.currentPage), totalCount, cumulativeOOSCount);
      }

      // fetch product count for redirect store on SLP if store_redirection is present
      // awaited so the count is in Redux state before SSR renderToString runs,
      // dispatched after SET_LISTING_DATA so the reducer reset doesn't wipe the count
      const storeRedirection = get(listingData, 'response.store_redirection');
      if (isSearch && storeRedirection) {
        try {
          const count = await getStoreRedirectCount({
            searchTerm: params.defaultParams[API_KEYS.SEARCH_TERM] || '',
            catalogTagFilter: storeRedirection,
            headers: ssrHeaders,
          });
          dispatch({ type: ActionTypes.SET_STORE_REDIRECT_COUNT, payload: count });
        } catch {
          /* non-critical, ignore */
        }
      }
    } catch (e) {
      if (e.status === 404) {
        dispatch({
          type: ActionTypes.SET_LISTING_ERROR,
          payload: {
            isFetchingError: false,
            isNotFound: true,
          },
        });
        dispatch(updateStatusCode(404));
      } else {
        dispatch({
          type: ActionTypes.SET_LISTING_ERROR,
          payload: {
            isFetchingError: true,
            isNotFound: false,
          },
        });
        dispatch(updateStatusCode(500));
      }
    }
    return;
  };
};
